import { useCallback, useState } from 'react';
import {
  Card,
  Box,
  Button,
  Text,
  Title,
  Icon,
  Tag,
  Spinner,
} from '@nimbus-ds/components';
import { CheckIcon, CloseIcon } from '@nimbus-ds/icons';
import { useTranslation } from 'react-i18next';
import { CurrencyType, PlanRecommendationPrice } from '@tiendanube/common';
import { formatCurrency } from 'commons/utils';
import useTranslationPreOnboarding from 'domains/PreOnboarding/useTranslationPreOnboarding';
import { SelectedPlanType } from '../../types';

interface PlanFeature {
  text: string;
  included: boolean;
}

interface PlanCardProps {
  type: string;
  title: string;
  price: PlanRecommendationPrice;
  description: string;
  features: PlanFeature[];
  buttonText: string;
  recommended?: boolean;
  onButtonClick: (planType: SelectedPlanType) => Promise<void>;
  showPaymentDisclaimer?: boolean;
}

export function PlanCard({
  type,
  title,
  price,
  description,
  features,
  buttonText,
  recommended = false,
  onButtonClick,
  showPaymentDisclaimer = false,
}: Readonly<PlanCardProps>) {
  const [isLoading, setIsLoading] = useState(false);
  const t = useTranslationPreOnboarding('recommendPlan');
  const {
    i18n: { language },
  } = useTranslation();

  const onClick = async () => {
    setIsLoading(true);
    await onButtonClick(type as SelectedPlanType);
    setIsLoading(false);
  };
  const priceValue = useCallback(
    (price: number, currency: CurrencyType, taxMsg: string) => {
      if (type === 'free') {
        return t('free');
      }
      const priceDisplay = formatCurrency(price, currency, language, true);
      return `${priceDisplay + taxMsg} / ${t('month')}`;
    },
    [language, t, type],
  );

  return (
    <Box
      borderColor={
        type === 'upgrade' ? 'primary-surfaceHighlight' : 'transparent'
      }
      borderStyle="solid"
      borderWidth="1"
      borderRadius="2"
      flex="1"
    >
      <Card>
        <Card.Header padding="none">
          <Box display="flex" flexDirection="column" gap="1">
            <Box display="flex" justifyContent="space-between">
              <Title as="h4">{title}</Title>
              {recommended && (
                <Tag appearance="primary">
                  <Text fontSize="caption" color="primary-textLow">
                    {t('featured')}
                  </Text>
                </Tag>
              )}
            </Box>
            <Text fontSize="highlight" color="neutral-textHigh">
              {priceValue(price.value, price.currency, price.taxMsg)}
            </Text>
            <Text fontSize="base" color="neutral-textLow">
              {description}
            </Text>
          </Box>
        </Card.Header>

        <Card.Body padding="none">
          <Box display="flex" flexDirection="column" gap="5">
            <Box
              my="1"
              borderColor="neutral-surfaceHighlight"
              borderStyle="solid"
              borderTopWidth="1"
            />
            <Box display="flex" flexDirection="column" gap="2">
              {features.map((feature) => (
                <Box key={feature.text} display="flex" gap="2">
                  <Icon
                    source={
                      feature.included ? (
                        <CheckIcon size="small" />
                      ) : (
                        <CloseIcon size="small" />
                      )
                    }
                    color={
                      feature.included
                        ? 'success-textLow'
                        : 'danger-interactive'
                    }
                  />
                  <Text fontSize="base" color="neutral-textLow">
                    {feature.text}
                  </Text>
                </Box>
              ))}
            </Box>
          </Box>
        </Card.Body>

        <Card.Footer padding="none">
          <Box display="flex" gap="4" alignItems="flex-start">
            <Box flex="1 0 auto">
              <Button
                appearance={recommended ? 'primary' : 'neutral'}
                onClick={onClick}
                disabled={isLoading}
              >
                {isLoading && <Spinner color="currentColor" size="small" />}
                {buttonText}
              </Button>
            </Box>
            {showPaymentDisclaimer && (
              <Box flex="0 1 auto">
                <Text
                  fontSize="caption"
                  lineHeight="caption"
                  color="neutral-textLow"
                >
                  {t('noCardRequired')}
                </Text>
              </Box>
            )}
          </Box>
        </Card.Footer>
      </Card>
    </Box>
  );
}
